import {
  adminLeadPipelinePageSizes,
  leadSourceKinds,
  type AdminLeadPipelineItem,
  type AdminLeadPipelineLanePage,
  type AdminLeadPipelineMoveOutcome,
  type AdminLeadPipelineMoveRequest,
  type LeadPipelineStage,
  type LeadSourceKind,
} from "@whitehaven/contracts";
import { isSafeLeadIdPathSegment } from "./lead-detail-route-model";
import { isBoundedOpaqueCrmCursor } from "./opaque-cursor-model";
import { isAdminLeadAssignmentSnapshotConsistent } from "./lead-assignment-model";
import {
  isLeadPipelineStage,
  leadPipelineStageOrder,
} from "./pipeline-stage-model";
import {
  adminLeadDuplicateSummariesEqual,
  isAdminLeadDuplicateSummaryConsistent,
} from "./admin-lead-duplicate-model";

export const MAX_LEAD_PIPELINE_REVISION = 2_147_483_647;

const leadSourceKindSet: ReadonlySet<string> = new Set(leadSourceKinds);

function isLeadSourceKind(value: unknown): value is LeadSourceKind {
  return typeof value === "string" && leadSourceKindSet.has(value);
}

function isCanonicalPipelineDisplayName(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 200 &&
    value === value.trim() &&
    !/[\u0000\n\r]/.test(value)
  );
}

function isCanonicalLeadSourceKey(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length >= 1 &&
    value.length <= 64 &&
    value === value.trim() &&
    value === value.toLowerCase() &&
    /^[a-z0-9._~-]+$/.test(value)
  );
}

export function isCanonicalAdminLeadPipelineTimestamp(
  value: unknown,
): value is string {
  if (
    typeof value !== "string" ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    return false;
  }

  const timestamp = new Date(value);
  return (
    Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value
  );
}

export function isAdminLeadPipelineRevision(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= MAX_LEAD_PIPELINE_REVISION
  );
}

export function isAdminLeadPipelineItemConsistent(
  value: unknown,
): value is AdminLeadPipelineItem {
  if (typeof value !== "object" || value === null) return false;
  const item = value as AdminLeadPipelineItem;
  const sourceIsConsistent =
    item.sourceKind === null
      ? item.sourceKey === null
      : isLeadSourceKind(item.sourceKind) &&
        (item.sourceKey === null || isCanonicalLeadSourceKey(item.sourceKey));

  return (
    isSafeLeadIdPathSegment(item.leadId) &&
    isCanonicalPipelineDisplayName(item.displayName) &&
    isLeadPipelineStage(item.pipelineStage) &&
    isAdminLeadPipelineRevision(item.pipelineRevision) &&
    isAdminLeadAssignmentSnapshotConsistent(item) &&
    isAdminLeadDuplicateSummaryConsistent(item.duplicateSummary) &&
    sourceIsConsistent &&
    isCanonicalAdminLeadPipelineTimestamp(item.updatedAt)
  );
}

export function compareAdminLeadPipelineItems(
  left: AdminLeadPipelineItem,
  right: AdminLeadPipelineItem,
): number {
  if (left.updatedAt !== right.updatedAt) {
    return left.updatedAt < right.updatedAt ? 1 : -1;
  }
  if (left.leadId === right.leadId) return 0;
  return left.leadId < right.leadId ? -1 : 1;
}

export function sortAdminLeadPipelineItems(
  items: readonly AdminLeadPipelineItem[],
): readonly AdminLeadPipelineItem[] {
  return [...items].sort(compareAdminLeadPipelineItems);
}

export function mergeAdminLeadPipelineItems(
  current: readonly AdminLeadPipelineItem[],
  incoming: readonly AdminLeadPipelineItem[],
): readonly AdminLeadPipelineItem[] | null {
  const merged = new Map(current.map((item) => [item.leadId, item] as const));

  for (const item of incoming) {
    if (merged.has(item.leadId)) return null;
    merged.set(item.leadId, item);
  }

  return sortAdminLeadPipelineItems([...merged.values()]);
}

function isAdminLeadPipelineLaneCollectionConsistent(
  value: unknown,
  expectedStage: LeadPipelineStage | undefined,
  maximumItems: number | null,
): value is AdminLeadPipelineLanePage {
  if (typeof value !== "object" || value === null) return false;
  const page = value as AdminLeadPipelineLanePage;
  if (
    !isLeadPipelineStage(page.pipelineStage) ||
    (expectedStage !== undefined && page.pipelineStage !== expectedStage) ||
    !Array.isArray(page.items) ||
    (maximumItems !== null && page.items.length > maximumItems) ||
    (page.items.length === 0 && page.nextCursor !== null) ||
    (page.nextCursor !== null && !isBoundedOpaqueCrmCursor(page.nextCursor))
  ) {
    return false;
  }

  const leadIds = new Set<string>();
  for (const item of page.items) {
    if (
      !isAdminLeadPipelineItemConsistent(item) ||
      item.pipelineStage !== page.pipelineStage ||
      leadIds.has(item.leadId)
    ) {
      return false;
    }
    leadIds.add(item.leadId);
  }

  const orderedItems = sortAdminLeadPipelineItems(page.items);
  return orderedItems.every((item, index) => item === page.items[index]);
}

export function isAdminLeadPipelineLanePageConsistent(
  value: unknown,
  expectedStage?: LeadPipelineStage,
): value is AdminLeadPipelineLanePage {
  return isAdminLeadPipelineLaneCollectionConsistent(
    value,
    expectedStage,
    adminLeadPipelinePageSizes[0],
  );
}

export function buildOrderedAdminLeadPipelineLanes(
  pages: readonly AdminLeadPipelineLanePage[],
): readonly AdminLeadPipelineLanePage[] | null {
  if (pages.length !== leadPipelineStageOrder.length) return null;

  const pagesByStage = new Map<LeadPipelineStage, AdminLeadPipelineLanePage>();
  const leadIds = new Set<string>();

  for (const page of pages) {
    if (
      !isLeadPipelineStage(page.pipelineStage) ||
      pagesByStage.has(page.pipelineStage) ||
      !isAdminLeadPipelineLaneCollectionConsistent(page, undefined, null)
    ) {
      return null;
    }

    for (const item of page.items) {
      if (leadIds.has(item.leadId)) return null;
      leadIds.add(item.leadId);
    }

    pagesByStage.set(page.pipelineStage, page);
  }

  const ordered = leadPipelineStageOrder.map((stage) =>
    pagesByStage.get(stage),
  );
  return ordered.every(
    (page): page is AdminLeadPipelineLanePage => page !== undefined,
  )
    ? ordered
    : null;
}

export function groupAdminLeadPipelineItems(
  items: readonly AdminLeadPipelineItem[],
): readonly AdminLeadPipelineLanePage[] | null {
  const leadIds = new Set<string>();
  const itemsByStage = new Map(
    leadPipelineStageOrder.map((stage) => [
      stage,
      [] as AdminLeadPipelineItem[],
    ]),
  );

  for (const item of items) {
    if (!isAdminLeadPipelineItemConsistent(item) || leadIds.has(item.leadId)) {
      return null;
    }

    leadIds.add(item.leadId);
    itemsByStage.get(item.pipelineStage)!.push(item);
  }

  return leadPipelineStageOrder.map((pipelineStage) => ({
    pipelineStage,
    items: sortAdminLeadPipelineItems(itemsByStage.get(pipelineStage)!),
    nextCursor: null,
  }));
}

export function isConfirmedAdminLeadPipelineMoveConsistent(
  current: AdminLeadPipelineItem,
  request: AdminLeadPipelineMoveRequest,
  value: unknown,
): value is Extract<AdminLeadPipelineMoveOutcome, { kind: "confirmed" }> {
  if (typeof value !== "object" || value === null) return false;
  const outcome = value as AdminLeadPipelineMoveOutcome;
  if (
    outcome.kind !== "confirmed" ||
    !isAdminLeadPipelineItemConsistent(current) ||
    !isSafeLeadIdPathSegment(request.leadId) ||
    !isLeadPipelineStage(request.expectedStage) ||
    !isLeadPipelineStage(request.targetStage) ||
    request.expectedStage === request.targetStage ||
    !isAdminLeadPipelineRevision(request.expectedPipelineRevision) ||
    !isCanonicalAdminLeadPipelineTimestamp(request.expectedUpdatedAt)
  ) {
    return false;
  }

  const { item, transition } = outcome;
  const nextRevision = request.expectedPipelineRevision + 1;

  return (
    current.leadId === request.leadId &&
    current.pipelineStage === request.expectedStage &&
    current.pipelineRevision === request.expectedPipelineRevision &&
    current.updatedAt === request.expectedUpdatedAt &&
    nextRevision <= MAX_LEAD_PIPELINE_REVISION &&
    isAdminLeadPipelineItemConsistent(item) &&
    item.leadId === request.leadId &&
    item.displayName === current.displayName &&
    item.sourceKind === current.sourceKind &&
    item.sourceKey === current.sourceKey &&
    item.assignedUserId === current.assignedUserId &&
    item.assignmentRevision === current.assignmentRevision &&
    item.assignedTo?.userId === current.assignedTo?.userId &&
    item.assignedTo?.displayLabel === current.assignedTo?.displayLabel &&
    item.assignedTo?.role === current.assignedTo?.role &&
    adminLeadDuplicateSummariesEqual(
      item.duplicateSummary,
      current.duplicateSummary,
    ) &&
    item.pipelineStage === request.targetStage &&
    item.pipelineRevision === nextRevision &&
    typeof transition === "object" &&
    transition !== null &&
    transition.fromStage === request.expectedStage &&
    transition.toStage === request.targetStage &&
    transition.pipelineRevision === nextRevision &&
    isCanonicalAdminLeadPipelineTimestamp(transition.occurredAt) &&
    item.updatedAt === transition.occurredAt &&
    item.updatedAt > request.expectedUpdatedAt
  );
}

export function applyConfirmedAdminLeadPipelineMove(
  pages: readonly AdminLeadPipelineLanePage[],
  request: AdminLeadPipelineMoveRequest,
  outcome: AdminLeadPipelineMoveOutcome,
): readonly AdminLeadPipelineLanePage[] | null {
  const orderedPages = buildOrderedAdminLeadPipelineLanes(pages);
  if (!orderedPages) return null;

  const matches = orderedPages.flatMap((page) =>
    page.items.filter((item) => item.leadId === request.leadId),
  );
  if (
    matches.length !== 1 ||
    !isConfirmedAdminLeadPipelineMoveConsistent(matches[0]!, request, outcome)
  ) {
    return null;
  }

  return orderedPages.map((page) => {
    const retained = page.items.filter(
      (item) => item.leadId !== request.leadId,
    );
    const items =
      page.pipelineStage === outcome.item.pipelineStage
        ? sortAdminLeadPipelineItems([...retained, outcome.item])
        : retained;

    return { ...page, items };
  });
}
